import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import OtpAuth from '../OtpAuth';
import { lembretesEnabled } from '../../lib/flags';
import { buildManagementAssessmentPayload, todayDateOnly } from '../management/ManagementAssessmentForm';
import { domainScoresFrom } from '../../lib/guidedFlow';

/**
 * "Salvar na ficha de um paciente", dentro da tela de resultado.
 *
 * O escore só sai do navegador quando a pessoa clica em salvar, e vai apenas
 * para a API autenticada da própria conta. Até lá nada é gravado, nem em URL
 * nem em localStorage. Sem sessão, o login é por código de e-mail, em um
 * passo e sem senha (mesmo fluxo OTP do cadastro), e o resultado continua na
 * tela durante todo o processo.
 *
 * Eventos: só calculator_save_clicked (pelo data-analytics-event do botão) e
 * assessment_created com flags — sem escore, respostas, escolaridade ou e-mail.
 */
async function api(path, options = {}) {
  const response = await fetch(path, { credentials: 'same-origin', ...options });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw Object.assign(new Error(payload.error || 'Não foi possível concluir a operação.'), { status: response.status });
  return payload;
}

export default function SaveToPatient({ total, education, reference, ageConfirm, blockScores, mode, variant = 'button', startSignal = 0 }) {
  const [phase, setPhase] = useState('closed'); // closed | checking | login | pick | saved
  const [patients, setPatients] = useState([]);
  const [query, setQuery] = useState('');
  const [patientId, setPatientId] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBirth, setNewBirth] = useState('');
  const [appliedAt, setAppliedAt] = useState(() => todayDateOnly());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [savedName, setSavedName] = useState('');
  const [lembrete, setLembrete] = useState("6"); // meses até o aviso de reavaliação; '' = sem aviso
  const [lembreteOk, setLembreteOk] = useState(false);

  const sectionRef = useRef(null);
  const headingRef = useRef(null);

  async function open() {
    setError('');
    setPhase('checking');
    try {
      const session = await api('/api/auth/session');
      setPhase(session.user ? 'pick' : 'login');
    } catch {
      setPhase('login');
    }
  }

  useEffect(() => {
    if (phase !== 'pick') return undefined;
    const term = query.trim();
    if (term && term.length < 3) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ limit: '10' });
        if (term) params.set('search', term);
        const payload = await api(`/api/management/summary?${params}`, { signal: controller.signal });
        setPatients(payload.patients || []);
        if (!term && !(payload.patients || []).length) setCreating(true);
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (e.status === 401) setPhase('login');
        else setError(e.message);
      }
    }, term ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [phase, query]);

  async function save(event) {
    event.preventDefault();
    setError('');
    if (!creating && !patientId) return setError('Escolha um paciente ou cadastre um novo.');
    if (creating && newName.trim().length < 2) return setError('Informe o nome do paciente.');
    if (appliedAt > todayDateOnly()) return setError('A data de aplicação não pode ser futura.');

    setBusy(true);
    try {
      let id = patientId;
      let name = patients.find((p) => String(p.id) === String(patientId))?.display_name || '';
      if (creating) {
        const created = await api('/api/management/summary', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ display_name: newName.trim(), birth_date: newBirth || null })
        });
        id = String(created.patient.id);
        name = created.patient.display_name || newName.trim();
      }
      const domains = mode === 'domains';
      await api(`/api/management/patients/${encodeURIComponent(id)}/assessments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildManagementAssessmentPayload({
          score: total,
          appliedAt,
          referenceKey: reference,
          observation: '',
          mode,
          education,
          ageConfirm,
          blockScores: domains ? domainScoresFrom(blockScores) : {}
        }))
      });
      window.eidocAnalytics?.capture('assessment_created', { mode, has_observation: false, has_block_scores: domains, origin: 'calculator_result' });
      window.eidocAnalytics?.capture('assessment_result_saved', {});
      // Aviso de reavaliação: melhor esforço, nunca derruba o salvamento. O e-mail não leva nome nem escore.
      if (lembrete && lembretesEnabled()) {
        try {
          await api('/api/lembretes', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ patient_id: id, meses: Number(lembrete) })
          });
          setLembreteOk(true);
          window.eidocAnalytics?.capture('reavaliacao_lembrete_criado', { meses: Number(lembrete) });
        } catch { setLembreteOk(false); }
      }
      setSavedName(name);
      setPhase('saved');
    } catch (e) {
      if (e.status === 401) setPhase('login');
      setError(e.message || 'Não foi possível salvar a avaliação.');
    } finally {
      setBusy(false);
    }
  }

  // Variante "hosted": o gatilho mora no rodapé do modal; aqui só aparece o fluxo depois do clique.
  useEffect(() => {
    if (startSignal > 0) open();
  }, [startSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (variant !== 'hosted' || phase === 'closed') return;
    sectionRef.current?.scrollIntoView?.({ block: 'nearest' });
    if (phase === 'login') headingRef.current?.focus({ preventScroll: true });
  }, [phase, variant]);

  if (phase === 'closed' && variant === 'hosted') return null;

  if (phase === 'closed') {
    return (
      <div>
        <button type="button" data-analytics-event="calculator_save_clicked" onClick={open} className="btn-secondary">
          Guardar no histórico do paciente
        </button>
        <p className="mt-2 max-w-[60ch] text-sm text-[var(--text-muted)]">
          A avaliação entra no histórico do paciente, com gráfico da pontuação ao longo do tempo. Nada sai deste navegador até você salvar; sem conta, basta um código enviado ao seu e-mail; não há senha.
        </p>
      </div>
    );
  }

  return (
    <section ref={sectionRef} aria-label="Guardar e acompanhar a evolução" className="border border-[var(--line)] bg-[var(--surface)] p-4 md:p-5">
      <h4 ref={headingRef} tabIndex={-1} className="font-display text-lg font-semibold outline-none">Guardar e acompanhar a evolução</h4>

      {phase === 'checking' && <p role="status" className="mt-2 text-sm text-[var(--text-muted)]">Verificando sua conta…</p>}

      {phase === 'login' && (
        <div className="mt-2 max-w-md">
          <p className="mb-3 text-sm text-[var(--text-muted)]">Informe seu e-mail. Enviamos um código de 6 dígitos e você continua daqui, com o resultado na tela.</p>
          <OtpAuth onAuthenticated={() => setPhase('pick')} />
          <p className="mt-3 text-xs text-[var(--text-muted)]">
            Seu e-mail será usado para autenticação e, com seu consentimento, enviado à lista de comunicação do MiniMental. Consulte a <Link className="underline" to="/politica-de-privacidade">política de privacidade</Link>.
          </p>
        </div>
      )}

      {phase === 'pick' && (
        <form onSubmit={save} className="mt-3 grid max-w-xl gap-4">
          {!creating ? (
            <div>
              <label htmlFor="salvar-busca" className="mb-1 block text-sm font-semibold">Paciente</label>
              <input id="salvar-busca" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar pelo nome (3 letras ou mais)" autoComplete="off" className="input-field" />
              <ul className="mt-2 max-h-56 overflow-auto border-t border-[var(--border)]" role="radiogroup" aria-label="Pacientes">
                {patients.map((p) => (
                  <li key={p.id} className="border-b border-[var(--border)]">
                    <label className={`flex min-h-12 cursor-pointer items-center gap-3 px-2 py-2 ${String(patientId) === String(p.id) ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--surface-soft)]'}`}>
                      <input type="radio" name="salvar-paciente" value={p.id} checked={String(patientId) === String(p.id)} onChange={() => setPatientId(String(p.id))} className="h-5 w-5 accent-[var(--accent)]" />
                      <span className="font-semibold">{p.display_name}</span>
                    </label>
                  </li>
                ))}
                {!patients.length && <li className="px-2 py-3 text-sm text-[var(--text-muted)]">Nenhum paciente encontrado.</li>}
              </ul>
              <button type="button" onClick={() => { setCreating(true); setPatientId(''); }} className="mt-1 min-h-11 text-sm text-[var(--primary)] underline">Cadastrar novo paciente</button>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="salvar-nome" className="mb-1 block text-sm font-semibold">Nome do paciente</label>
                <input id="salvar-nome" value={newName} onChange={(e) => setNewName(e.target.value)} autoComplete="off" maxLength={160} className="input-field" />
              </div>
              <div>
                <label htmlFor="salvar-nasc" className="mb-1 block text-sm font-semibold">Nascimento <span className="font-normal text-[var(--text-muted)]">(opcional)</span></label>
                <input id="salvar-nasc" type="date" max={todayDateOnly()} value={newBirth} onChange={(e) => setNewBirth(e.target.value)} className="input-field" />
              </div>
              {patients.length > 0 && <button type="button" onClick={() => setCreating(false)} className="min-h-11 self-end text-left text-sm text-[var(--primary)] underline">Escolher paciente já cadastrado</button>}
            </div>
          )}

          <div>
            <label htmlFor="salvar-data" className="mb-1 block text-sm font-semibold">Data da aplicação</label>
            <input id="salvar-data" type="date" required max={todayDateOnly()} value={appliedAt} onChange={(e) => setAppliedAt(e.target.value)} className="input-field w-auto" />
          </div>

          {lembretesEnabled() && <div>
            <label htmlFor="salvar-lembrete" className="mb-1 block text-sm font-semibold">Avisar-me para reavaliar</label>
            <select id="salvar-lembrete" value={lembrete} onChange={(e) => setLembrete(e.target.value)} className="input-field w-auto">
              <option value="3">em 3 meses</option>
              <option value="6">em 6 meses</option>
              <option value="12">em 12 meses</option>
              <option value="">não avisar</option>
            </select>
            <p className="mt-1 max-w-[60ch] text-xs text-[var(--text-muted)]">Um e-mail único para você, sem o nome nem a pontuação do paciente, com link para cancelar.</p>
          </div>}

          {error && <p role="alert" className="rounded bg-[var(--danger-bg)] px-3 py-2 text-sm font-semibold text-[var(--danger-text)]">{error}</p>}
          <div>
            <button type="submit" disabled={busy} className="btn-primary">{busy ? 'Salvando…' : 'Salvar avaliação'}</button>
          </div>
        </form>
      )}

      {phase === 'saved' && (
        <p role="status" className="mt-2">
          Avaliação salva na ficha de <strong>{savedName || 'paciente'}</strong>.{lembreteOk ? ` Avisaremos por e-mail em ${lembrete} meses, na hora de reavaliar.` : ''}{' '}
          <Link to="/dashboard" className=" text-[var(--primary)] underline">Abrir ficha</Link>
        </p>
      )}
    </section>
  );
}
